import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Mail, ArrowRight } from 'lucide-react';
import styled from 'styled-components';
import { Container } from '../styled/Container';
import { regionFromPathname } from '../../utils/regionFromPath';
import { pathWithRegion } from '../../utils/navBase';
import { categoriesHubPath, similarTypePath } from '../../utils/categoriesPaths';
import { useI18n } from '../../hooks/useI18n';

const FooterEl = styled.footer`
  background: ${({ theme }) => theme.colors.primary};
  margin-top: 5rem;
`;

const Inner = styled(Container)`
  padding-top: 3.5rem;
  padding-bottom: 3rem;
`;

const NewsletterBox = styled.div`
  background: ${({ theme }) => theme.colors.maroon};
  border-radius: ${({ theme }) => theme.radiusSm};
  padding: 2rem;
  margin-bottom: 3rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  @media (min-width: 768px) {
    padding: 2.5rem;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const NewsletterTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontClamp.xl};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.maroonForeground};
  margin: 0;
`;

const NewsletterDesc = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: rgba(255, 255, 255, 0.72);
  margin: 0.35rem 0 0 0;
`;

const NewsletterForm = styled.form`
  display: flex;
  gap: 0.5rem;
  width: 100%;
  flex-wrap: wrap;
  @media (min-width: 768px) {
    width: auto;
    flex-wrap: nowrap;
  }
`;

const EmailWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 200px;
  @media (min-width: 768px) {
    width: 16rem;
  }
`;

const EmailIcon = styled.span`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
  display: flex;
`;

const EmailInput = styled.input`
  width: 100%;
  height: 2.75rem;
  padding-left: 2.5rem;
  padding-right: 1rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: #fff;
  color: ${({ theme }) => theme.colors.foreground};
  font-size: 0.875rem;
  border: none;
  outline: none;
`;

const SubscribeBtn = styled.button`
  height: 2.75rem;
  padding: 0 1.5rem;
  border: none;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.gold};
  color: ${({ theme }) => theme.colors.goldForeground};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  display: flex;
  align-items: center;
  gap: 0.35rem;
  white-space: nowrap;
  cursor: pointer;
  &:hover {
    opacity: 0.92;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const LogoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  margin-bottom: 1rem;
`;

const LogoBox = styled.div`
  width: 2.25rem;
  height: 2.25rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.gold};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.goldForeground};
`;

const LogoText = styled.span`
  font-size: ${({ theme }) => theme.fontClamp.lg};
  font-weight: 800;
  color: ${({ theme }) => theme.colors.primaryForeground};
`;

const BrandDesc = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: rgba(255, 255, 255, 0.58);
  line-height: 1.6;
  margin: 0;
`;

const ColumnTitle = styled.h4`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gold};
  margin: 0 0 1rem 0;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const LinkList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
`;

const FooterLink = styled(Link)`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: rgba(255, 255, 255, 0.58);
  text-decoration: none;
  transition: color 0.2s;
  &:hover {
    color: #fff;
  }
`;

const Bottom = styled.div`
  margin-top: 3rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
  }
`;

const Copyright = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.xs};
  color: rgba(255, 255, 255, 0.38);
  margin: 0;
`;

const BottomLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1.25rem;
`;

const BottomLink = styled(Link)`
  font-size: ${({ theme }) => theme.fontClamp.xs};
  color: rgba(255, 255, 255, 0.38);
  text-decoration: none;
  &:hover {
    color: rgba(255, 255, 255, 0.65);
  }
`;

export default function Footer() {
  const [email, setEmail] = useState('');
  const { pathname } = useLocation();
  const region = regionFromPathname(pathname);
  const { t } = useI18n();
  const p = (path) => pathWithRegion(path, region);
  const home = p('/');

  return (
    <FooterEl>
      <Inner>
        <NewsletterBox>
          <div>
            <NewsletterTitle>{t('footer.newsletterTitle', 'Stay in the loop')}</NewsletterTitle>
            <NewsletterDesc>
              {t('footer.newsletterDesc', 'Get occasional updates on new similarity features and curated picks.')}
            </NewsletterDesc>
          </div>
          <NewsletterForm onSubmit={(e) => e.preventDefault()}>
            <EmailWrap>
              <EmailIcon>
                <Mail size={16} />
              </EmailIcon>
              <EmailInput
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('footer.emailPlaceholder', 'you@example.com')}
              />
            </EmailWrap>
            <SubscribeBtn type="button">
              {t('footer.subscribe', 'Subscribe')} <ArrowRight size={14} />
            </SubscribeBtn>
          </NewsletterForm>
        </NewsletterBox>

        <Grid>
          <div>
            <LogoRow>
              <LogoBox>F</LogoBox>
              <LogoText>FliqMatch</LogoText>
            </LogoRow>
            <BrandDesc>
              {t('footer.brandDesc', 'Find similar movies, TV shows, and anime with community-ranked picks.')}
            </BrandDesc>
          </div>
          <div>
            <ColumnTitle>{t('footer.explore', 'Explore')}</ColumnTitle>
            <LinkList>
              <FooterLink to={categoriesHubPath(region)}>{t('nav.categories', 'Categories')}</FooterLink>
              <FooterLink to={similarTypePath('movies', region)}>{t('nav.similarMovies', 'Similar movies')}</FooterLink>
              <FooterLink to={similarTypePath('tv', region)}>{t('nav.similarTv', 'Similar TV shows')}</FooterLink>
              <FooterLink to={similarTypePath('anime', region)}>{t('nav.similarAnime', 'Similar anime')}</FooterLink>
              <FooterLink to={similarTypePath('anime-movies', region)}>
                {t('nav.similarAnimeMovies', 'Similar anime movies')}
              </FooterLink>
              <FooterLink to={similarTypePath('anime-tv', region)}>
                {t('nav.similarAnimeTv', 'Similar anime TV shows')}
              </FooterLink>
              <FooterLink to={p('/search')}>{t('common.search', 'Search')}</FooterLink>
            </LinkList>
          </div>
          <div>
            <ColumnTitle>{t('footer.popularSimilar', 'Popular similar')}</ColumnTitle>
            <LinkList>
              <FooterLink to={p('/top/movies')}>{t('top.movies.title', 'Top movie matches')}</FooterLink>
              <FooterLink to={p('/top/tv')}>{t('top.tv.title', 'Top TV matches')}</FooterLink>
              <FooterLink to={p('/top/anime-movies')}>
                {t('top.animeMovies.title', 'Top anime movie matches')}
              </FooterLink>
              <FooterLink to={p('/top/anime-tv')}>
                {t('top.animeTv.title', 'Top anime TV matches')}
              </FooterLink>
            </LinkList>
          </div>
          <div>
            <ColumnTitle>{t('footer.company', 'Company')}</ColumnTitle>
            <LinkList>
              <FooterLink to={p('/about')}>{t('nav.about', 'About')}</FooterLink>
              <FooterLink to={p('/contact')}>{t('static.contact.title', 'Contact')}</FooterLink>
              <FooterLink to={p('/privacy')}>{t('static.privacy.title', 'Privacy')}</FooterLink>
              <FooterLink to={p('/terms')}>{t('static.terms.title', 'Terms')}</FooterLink>
              <FooterLink to={p('/faq')}>{t('static.faq.title', 'FAQ')}</FooterLink>
            </LinkList>
          </div>
        </Grid>

        <Bottom>
          <Copyright>© {new Date().getFullYear()} FliqMatch</Copyright>
          <BottomLinks>
            <BottomLink to={p('/privacy')}>{t('static.privacy.title', 'Privacy')}</BottomLink>
            <BottomLink to={p('/terms')}>{t('static.terms.title', 'Terms')}</BottomLink>
            <BottomLink to={p('/contact')}>{t('static.contact.title', 'Contact')}</BottomLink>
          </BottomLinks>
        </Bottom>
      </Inner>
    </FooterEl>
  );
}
